import { type ReactNode } from "react";

import { IconX } from "@btnet/blueprint-icons";
import { cn } from "@btnet/utilities-core";

import "./BuiPill.less";
import { type IBuiPillProps } from "./BuiPill.types";

const pillClassNames = (
  isActionable: boolean,
  icon?: ReactNode,
  avatar?: ReactNode,
  className?: string,
): string => {
  return cn("bui-pill", className, {
    "bui-pill-actionable-icon": isActionable && icon,
    "bui-pill-actionable-text": isActionable && !avatar && !icon,
    "bui-pill-actionable-avatar": isActionable && avatar,
    "bui-pill-readonly-icon": !isActionable && icon && !avatar,
    "bui-pill-readonly-text": !isActionable && !avatar && !icon,
    "bui-pill-readonly-avatar": !isActionable && avatar,
  });
};

// Space activates on keyUp like Enter, but its default keyDown behavior scrolls
// the page, so we must preventDefault on keyDown while still firing on keyUp.
const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>): void => {
  if (event.key === " ") {
    event.preventDefault();
  }
};

const handleKeyUp = (
  event: React.KeyboardEvent<HTMLDivElement>,
  isActionable: boolean,
  onClick:
    | ((
        event:
          | React.MouseEvent<HTMLDivElement>
          | React.KeyboardEvent<HTMLDivElement>,
      ) => void)
    | undefined,
): void => {
  if (isActionable && onClick && (event.key === "Enter" || event.key === " ")) {
    onClick(event);
  }
};

/**
 * BuiPill - the blueprint (BUI) pill/chip primitive.
 *
 * Renders a compact, rounded label that can optionally lead with an icon or
 * avatar and, when `isActionable`, expose a trailing close button for
 * removal/dismissal. The actionable area is keyboard accessible (`role="button"`,
 * focusable, and triggers `onClick` on Enter and Space).
 *
 * Prefer the `BTPill` wrapper (in `@btnet/core-components`) for app usage, as it
 * adds tooltip and `BdsAvatar` configuration on top of this primitive.
 *
 * @param props - {@link IBuiPillProps} controlling the pill's content, appearance, and behavior.
 * @returns The rendered pill element.
 * @example
 * <BuiPill testid="status-pill" text="Active" icon={<IconTag />} />
 * @example
 * <BuiPill
 *   testid="filter-pill"
 *   text="Jane Doe"
 *   isActionable
 *   ariaLabel="Remove Jane Doe"
 *   onClick={handleRemove}
 * />
 */
export const BuiPill: React.FC<IBuiPillProps> = ({
  testid,
  text,
  isActionable = false,
  isDisabled = false,
  avatar,
  onClick,
  icon,
  ariaLabel,
  className,
  style,
}) => {
  return (
    <div
      data-testid={testid}
      className={pillClassNames(isActionable, icon, avatar, className)}
      style={style}
    >
      {avatar && <div className="bui-pill--avatar">{avatar}</div>}

      {icon && !avatar && <div className="bui-pill--icon">{icon}</div>}

      <div className="bui-pill--text">{text}</div>

      {isActionable && (
        <div
          className={cn("bui-pill--action", {
            "bui-pill--action-disabled": isDisabled,
          })}
          role="button"
          tabIndex={0}
          aria-disabled={isDisabled && isActionable}
          aria-label={ariaLabel}
          onClick={isDisabled ? undefined : onClick}
          onKeyDown={isDisabled ? undefined : handleKeyDown}
          onKeyUp={
            isDisabled
              ? undefined
              : (event) => handleKeyUp(event, isActionable, onClick)
          }
        >
          {
            <IconX
              testid={`${testid}-action`}
              className="bui-pill--action-icon"
              size="sm"
            />
          }
        </div>
      )}
    </div>
  );
};
